import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import type { ContextMenuData } from '../../types';
import { capitalize } from '../../utils/capitalize';

interface ContextMenuProps {
    contextMenu: ContextMenuData | null;
    handleContextAction: (type: ContextMenuData['type'], action: string, target?: string) => void;
    closeContextMenu: () => void;
}

const ContextMenuItem = React.memo(({ 
  label, 
  icon, 
  onClick 
}: { 
  label: string; 
  icon?: string; 
  onClick: () => void; 
}) => {
  return (
    <button
      onClick={onClick}
      className="block w-full text-left px-4 py-3 text-sm text-white hover:bg-slate-700 transition-colors border-b border-slate-700 last:border-0"
    >
      {icon && <span className="inline-block w-5 text-center mr-2">{icon}</span>}
      {label}
    </button>
  );
});

const ContextMenu: React.FC<ContextMenuProps> = ({ contextMenu, handleContextAction, closeContextMenu }) => {
    const menuRef = useRef<HTMLDivElement>(null);
    const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({
        position: 'fixed',
        opacity: 0,
        pointerEvents: 'none',
    });

    useEffect(() => {
        if (contextMenu && menuRef.current) {
            const { x, y } = contextMenu;
            const menuRect = menuRef.current.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            let top = y;
            let left = x;

            // Prevent going off right edge
            if (left + menuRect.width > viewportWidth) {
                left = x - menuRect.width;
            }

            // Prevent going off bottom edge
            if (top + menuRect.height > viewportHeight) {
                top = y - menuRect.height;
            }
            
            // Prevent going off top/left edges (safety)
            if (left < 10) left = 10;
            if (top < 10) top = 10;

            setMenuStyle({
                top: `${top}px`,
                left: `${left}px`,
                position: 'fixed',
                zIndex: 99999, // Increased z-index to be safe
                opacity: 1,
                pointerEvents: 'auto'
            });
        }
    }, [contextMenu]);

    if (!contextMenu) return null;

    return ReactDOM.createPortal(
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 z-[99990]"
                onClick={closeContextMenu}
                onContextMenu={(e) => { e.preventDefault(); closeContextMenu(); }}
                style={{ pointerEvents: 'auto', backgroundColor: 'transparent' }}
            />

            {/* Menu */}
            <div
                ref={menuRef}
                className="context-menu-container fixed border-2 border-sworn-brown rounded-lg shadow-2xl py-1 w-56 flex flex-col bg-slate-800 text-white"
                style={menuStyle}
                onClick={(e) => e.stopPropagation()}
            >
                {contextMenu.type === 'knight' && Array.isArray(contextMenu.options) && (
                    <>
                        {contextMenu.options.map((option: string) => {
                            const label = `Select ${capitalize(option)}`;
                            
                            return (
                                <ContextMenuItem
                                    key={option}
                                    label={label}
                                    icon="👤"
                                    onClick={() => handleContextAction(contextMenu.type, option, contextMenu.target)}
                                />
                            );
                        })}
                    </>
                )}

                {contextMenu.type === 'build' && Array.isArray(contextMenu.options) && (
                    <>
                        {contextMenu.options.map((option: string) => {
                            return (
                                <ContextMenuItem
                                    key={option}
                                    label={option}
                                    onClick={() => handleContextAction(contextMenu.type, option, contextMenu.target)}
                                />
                            );
                        })}
                    </>
                )}
                
                {contextMenu.type === 'fae' && (
                    <ContextMenuItem
                        label={`View ${capitalize(contextMenu.target || '')} Blessings`}
                        icon="👁️"
                        onClick={() => handleContextAction(contextMenu.type, 'view', contextMenu.target)}
                    />
                )}

                {(contextMenu.type === 'weapon' || contextMenu.type === 'spell') && contextMenu.options && (
                    <>
                        {contextMenu.options.map((option: string) => {
                             return (
                                <ContextMenuItem
                                    key={option}
                                    label={option.split('_').map(capitalize).join(' ')}
                                    icon={contextMenu.type === 'weapon' ? '⚔️' : '⚡'}
                                    onClick={() => handleContextAction(contextMenu.type, option)}
                                />
                            );
                        })}
                    </>
                )}

                {contextMenu.type === 'blessing' && (
                    <ContextMenuItem
                        label="View Blessing Details"
                        icon="👁️"
                        onClick={() => handleContextAction(contextMenu.type, 'view', contextMenu.target)}
                    />
                )}
                
                {contextMenu.type === 'modifier' && (
                    <ContextMenuItem
                        label="Show Info"
                        icon="🛡️"
                        onClick={() => handleContextAction(contextMenu.type, 'info', contextMenu.target)}
                    />
                )}
            </div>
        </>,
        document.body
    );
};

export default ContextMenu;
